import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import {
  RefreshCw,
  CheckCircle2,
  XCircle,
  Trash2,
  ExternalLink,
  Loader2,
  Copy,
  Scale,
  Newspaper,
} from "lucide-react";
import {
  adminListLegalUpdates,
  adminSetLegalUpdateStatus,
  adminDeleteLegalUpdates,
  adminAutoResolveDuplicates,
  adminTriggerLegalRefresh,
} from "@/lib/api/legal-updates.functions";

export const Route = createFileRoute(
  "/_authenticated/portal/admin/legal-updates",
)({
  component: LegalUpdatesAdmin,
});

type StatusFilter = "pending" | "approved" | "dismissed" | "all";
type SourceFilter = "saflii" | "gazette" | "all";

function LegalUpdatesAdmin() {
  const list = useServerFn(adminListLegalUpdates);
  const setStatus = useServerFn(adminSetLegalUpdateStatus);
  const del = useServerFn(adminDeleteLegalUpdates);
  const dedupe = useServerFn(adminAutoResolveDuplicates);
  const refresh = useServerFn(adminTriggerLegalRefresh);
  const qc = useQueryClient();

  const [status, setStatusFilter] = useState<StatusFilter>("pending");
  const [source, setSource] = useState<SourceFilter>("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [slugToRefresh, setSlugToRefresh] = useState("");

  const query = useQuery({
    queryKey: ["admin-legal-updates", status, source],
    queryFn: () => list({ data: { status, source, limit: 300 } }),
  });

  const items = query.data?.items ?? [];
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((i: any) =>
      [i.title, i.summary, i.url, i.external_id]
        .filter(Boolean)
        .some((v: string) => String(v).toLowerCase().includes(needle)),
    );
  }, [items, q]);

  // Duplicate detection: same (source, normalised title) → mark all but the
  // oldest as duplicates.
  const duplicates = useMemo(() => {
    const seen = new Map<string, string>();
    const dupIds = new Set<string>();
    // Iterate oldest first to keep the earliest row.
    const sorted = [...items].sort(
      (a: any, b: any) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );
    for (const r of sorted) {
      const key = `${r.source}::${(r.title ?? "").trim().toLowerCase()}`;
      if (seen.has(key)) dupIds.add(r.id);
      else seen.set(key, r.id);
    }
    return dupIds;
  }, [items]);

  const setStatusMutation = useMutation({
    mutationFn: (args: { id: string; status: "approved" | "dismissed" | "pending" }) =>
      setStatus({ data: args }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-legal-updates"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (ids: string[]) => del({ data: { ids } }),
    onSuccess: () => {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["admin-legal-updates"] });
    },
  });

  const dedupeMutation = useMutation({
    mutationFn: () => dedupe(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-legal-updates"] }),
  });

  const refreshMutation = useMutation({
    mutationFn: (slug?: string) => refresh({ data: slug ? { slug } : {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-legal-updates"] }),
  });

  function toggle(id: string) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  function selectAllDuplicates() {
    setSelected(new Set(duplicates));
  }

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-xl text-navy">
            Incoming Acts & court updates
          </h2>
          <p className="text-xs text-muted-foreground">
            SAFLII case feeds + gov.za gazette. Approve into the site,
            dismiss noise, remove duplicates.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => refreshMutation.mutate(undefined)}
            disabled={refreshMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-navy px-3 py-1.5 text-xs font-semibold text-navy-foreground disabled:opacity-60"
          >
            {refreshMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            Refresh feeds now
          </button>
          <button
            onClick={() => dedupeMutation.mutate()}
            disabled={dedupeMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-navy px-3 py-1.5 text-xs font-semibold text-navy disabled:opacity-60"
          >
            <Copy className="h-3.5 w-3.5" /> Auto-resolve duplicates
          </button>
        </div>
      </div>

      {/* Legislation cache refresher */}
      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="font-semibold text-navy text-sm">Refresh a cached Act</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Clear the cached full text for a specific Act slug so the next
          reader re-fetches from gov.za. Slug format:{" "}
          <code>labour-relations-act-66-of-1995</code>.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <input
            value={slugToRefresh}
            onChange={(e) => setSlugToRefresh(e.target.value)}
            placeholder="act-slug"
            className="flex-1 min-w-[240px] rounded-md border border-input bg-background px-3 py-1.5 text-sm"
          />
          <button
            onClick={() =>
              slugToRefresh && refreshMutation.mutate(slugToRefresh)
            }
            disabled={!slugToRefresh || refreshMutation.isPending}
            className="rounded-md bg-navy px-3 py-1.5 text-xs font-semibold text-navy-foreground disabled:opacity-60"
          >
            Invalidate cache
          </button>
        </div>
        {refreshMutation.data && (
          <p className="mt-2 text-xs text-emerald-700">
            Done: {JSON.stringify(refreshMutation.data)}
          </p>
        )}
        {dedupeMutation.data && (
          <p className="mt-1 text-xs text-emerald-700">
            Auto-dedupe removed {dedupeMutation.data.deleted} row
            {dedupeMutation.data.deleted === 1 ? "" : "s"}.
          </p>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={status}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
        >
          <option value="pending">Pending review</option>
          <option value="approved">Approved</option>
          <option value="dismissed">Dismissed</option>
          <option value="all">All</option>
        </select>
        <select
          value={source}
          onChange={(e) => setSource(e.target.value as SourceFilter)}
          className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
        >
          <option value="all">All sources</option>
          <option value="saflii">SAFLII (cases)</option>
          <option value="gazette">Gazette (Acts)</option>
        </select>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search title, summary…"
          className="flex-1 min-w-[220px] rounded-md border border-input bg-background px-3 py-1.5 text-sm"
        />
        <span className="text-xs text-muted-foreground">
          {filtered.length} of {items.length}
          {duplicates.size > 0 && ` · ${duplicates.size} duplicate(s)`}
        </span>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm">
          <span className="font-medium text-amber-900">
            {selected.size} selected
          </span>
          <button
            onClick={() => deleteMutation.mutate(Array.from(selected))}
            disabled={deleteMutation.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-red-600 px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-60"
          >
            <Trash2 className="h-3.5 w-3.5" /> Delete selected
          </button>
          <button
            onClick={() => setSelected(new Set())}
            className="text-xs text-amber-900 underline"
          >
            Clear
          </button>
        </div>
      )}

      {duplicates.size > 0 && selected.size === 0 && (
        <button
          onClick={selectAllDuplicates}
          className="text-xs text-navy underline"
        >
          Select all {duplicates.size} suspected duplicate
          {duplicates.size === 1 ? "" : "s"}
        </button>
      )}

      {query.isLoading && (
        <p className="text-sm text-muted-foreground">Loading…</p>
      )}
      {query.error && (
        <p className="text-sm text-red-600">
          {(query.error as Error).message}
        </p>
      )}

      <ul className="space-y-2">
        {filtered.map((i: any) => {
          const isDup = duplicates.has(i.id);
          return (
            <li
              key={i.id}
              className={`rounded-lg border p-3 ${
                isDup
                  ? "border-amber-300 bg-amber-50/40"
                  : "border-border bg-card"
              }`}
            >
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={selected.has(i.id)}
                  onChange={() => toggle(i.id)}
                  className="mt-1"
                  aria-label={`Select ${i.title}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    {i.source === "saflii" ? (
                      <Scale className="h-3.5 w-3.5 text-navy" />
                    ) : (
                      <Newspaper className="h-3.5 w-3.5 text-navy" />
                    )}
                    <span className="rounded-full bg-navy/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-navy">
                      {i.source} · {i.kind}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${
                        i.status === "approved"
                          ? "bg-emerald-100 text-emerald-800"
                          : i.status === "dismissed"
                            ? "bg-zinc-200 text-zinc-700"
                            : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {i.status}
                    </span>
                    {isDup && (
                      <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-900">
                        duplicate
                      </span>
                    )}
                    {i.published_at && (
                      <span className="text-[11px] text-muted-foreground">
                        {new Date(i.published_at).toLocaleDateString("en-ZA")}
                      </span>
                    )}
                  </div>
                  <h3 className="mt-1 text-sm font-semibold text-navy">
                    {i.title}
                  </h3>
                  {i.summary && (
                    <p className="mt-1 text-xs text-foreground/75 line-clamp-3">
                      {i.summary}
                    </p>
                  )}
                  {i.url && (
                    <a
                      href={i.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 inline-flex items-center gap-1 text-xs text-navy hover:underline"
                    >
                      Open source <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() =>
                      setStatusMutation.mutate({
                        id: i.id,
                        status: "approved",
                      })
                    }
                    disabled={setStatusMutation.isPending}
                    className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-[11px] font-medium text-white disabled:opacity-60"
                  >
                    <CheckCircle2 className="h-3 w-3" /> Approve
                  </button>
                  <button
                    onClick={() =>
                      setStatusMutation.mutate({
                        id: i.id,
                        status: "dismissed",
                      })
                    }
                    disabled={setStatusMutation.isPending}
                    className="inline-flex items-center gap-1 rounded-md border border-input px-2 py-1 text-[11px] font-medium disabled:opacity-60"
                  >
                    <XCircle className="h-3 w-3" /> Dismiss
                  </button>
                  <button
                    onClick={() => deleteMutation.mutate([i.id])}
                    disabled={deleteMutation.isPending}
                    className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[11px] font-medium text-red-700 disabled:opacity-60"
                  >
                    <Trash2 className="h-3 w-3" /> Delete
                  </button>
                </div>
              </div>
            </li>
          );
        })}
        {filtered.length === 0 && !query.isLoading && (
          <li className="rounded-lg border border-dashed border-input bg-muted/20 p-6 text-center text-sm text-muted-foreground">
            No updates match this filter.
          </li>
        )}
      </ul>
    </section>
  );
}
